/**
 * One dice set, live: six real dice on a canvas the size of the set's tile,
 * the d20 large in the middle and the other five in a row beneath. This is
 * the close-up in the store's Dice tab, and it is also how the shelf's still
 * previews are baked (`bakeShelfPreview`), so what the still shows and what
 * comes alive under the pointer are the same picture down to the pixel.
 *
 * The camera frames the tile's width and sits centred on it; the dice are
 * placed by ray through the layout's pixel spots onto the resting plane, so
 * the layout holds at any tile size. Handling: hovering a die floats it a
 * little and turns it slowly; pressing lifts it high and the pointer turns it
 * about the screen axes; let go and it keeps a little spin, sheds it and
 * settles back into its resting pose.
 *
 * The skin's atlases come from the worker (`skinDieAssetsAsync`) so opening
 * a set never hitches the page; `ready` resolves once all six dice stand and
 * the studio light is in.
 */
import * as THREE from 'three';

import { FLOOR_Y } from './animate';
import { DiceGL } from './diceRenderer';
import { cloneDieMaterial, releaseSkinDieAssets, skinDieAssets, skinDieAssetsAsync } from './dieAssets';
import { dieGeometry, faceIndexForValue, faceQuaternion } from './geometry';

/** Half the world width the camera frames. The dice are scaled to the tile inside it. */
const HALF_WIDTH = 4;
/** Atlas scale 1: the d20 here is smaller than the roll-off's, and scale 2 is a 2560px cut per skin. */
const ATLAS_SCALE = 1;
/**
 * Where each die sits inside the tile, as a share of the tile's width and
 * height, and its radius as a share of the tile's width.
 */
const D20_AT = [0.5, 0.42];
const D20_RADIUS = 0.15;
const SMALL_ROW_Y = 0.8;
const SMALL_RADIUS = 0.062;
const SMALL_ORDER = [4, 6, 8, 10, 12];
/** Turned a little off square so three faces read from the front. */
const YAW = 0.55;
/** Lifts in radii: a hovered die floats a little, a held one rides high under the pointer. */
const HOVER_LIFT = 0.18;
const HELD_LIFT = 0.9;
const HOVER_SPIN = 0.9;
/** Radians per canvas pixel of pointer travel while a die is held. */
const DRAG_TURN = 0.012;
/** How fast a released die sheds its spin, and how fast it eases back to its resting pose. */
const SPIN_DRAG = 3.2;
const SETTLE = 4;
/** The still preview's size: the tile's 16:10, with room to spare for the close-up on a dense display. */
export const SHELF_PREVIEW_WIDTH = 1600;
export const SHELF_PREVIEW_HEIGHT = 1000;

const _up = new THREE.Vector3(0, 1, 0);
const _ndc = new THREE.Vector2();
const _v = new THREE.Vector3();
const _axis = new THREE.Vector3();
const _q = new THREE.Quaternion();
const YAW_TURN = new THREE.Quaternion().setFromAxisAngle(_up, YAW);

/**
 * Where a set's six dice sit inside a tile of `width` x `height` pixels:
 * centre and radius per side count, in pixels from the tile's top-left.
 */
export function shelfLayout(width, height) {
  const out = [{ sides: 20, x: D20_AT[0] * width, y: D20_AT[1] * height, radius: D20_RADIUS * width }];
  const n = SMALL_ORDER.length;
  SMALL_ORDER.forEach((sides, i) => {
    out.push({ sides, x: ((i + 0.5) / n) * width, y: SMALL_ROW_Y * height, radius: SMALL_RADIUS * width });
  });
  return out;
}

/** The still a skin's tile shows, baked from this rig; beside its tiles under `public/dice/skins/`. */
export function shelfPreviewUrl(skin) {
  return skin.tile ? skin.tile.replace(/\.webp$/, '-shelf.webp') : null;
}

export class DiceSetRig extends DiceGL {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {object} skin  the recipe from `DICE_SKINS`
   * @param {{ sync?: boolean }} [opts]  `sync` cuts the atlases on the main
   *   thread, for the lab, whose recipe under authoring carries canvases the
   *   worker cannot be handed.
   */
  constructor(canvas, skin, { sync = false } = {}) {
    super(canvas, HALF_WIDTH);
    this.skin = skin;
    this.raycaster = new THREE.Raycaster();
    this.dice = [];
    this.hovered = null;
    this.held = null;
    this.width = 1;
    this.height = 1;
    this.disposed = false;
    const bodies = shelfLayout(1, 1).map(({ sides }) => {
      const built = sync
        ? Promise.resolve(skinDieAssets(sides, ATLAS_SCALE, skin))
        : skinDieAssetsAsync(sides, ATLAS_SCALE, skin);
      return built.then((assets) => {
        if (this.disposed) return;
        this.dice.push(this.makeDie(sides, assets));
        this.place();
      });
    });
    /** All six dice standing and the studio light in. */
    this.ready = Promise.all([...bodies, this.environmentReady]).then(() => {});
  }

  makeDie(sides, { geometry: body, material }) {
    const geometry = dieGeometry(sides);
    const mesh = new THREE.Mesh(body, cloneDieMaterial(material));
    mesh.castShadow = true;
    const flat = faceQuaternion(geometry, faceIndexForValue(geometry, sides));
    const base = new THREE.Quaternion(flat.x, flat.y, flat.z, flat.w).premultiply(YAW_TURN);
    let lowest = Infinity;
    for (const p of geometry.vertices) {
      lowest = Math.min(lowest, _v.set(p[0], p[1], p[2]).applyQuaternion(base).y);
    }
    mesh.quaternion.copy(base);
    this.scene.add(mesh);
    return {
      sides, mesh, base,
      /** The centre's height over the floor per unit radius, in the resting pose. */
      rest: -lowest,
      radius: 1, px: 0, py: 0, lift: 0, spin: 0,
      /** The pose the shopper turned it into, and the spin still left in it. */
      turn: new THREE.Quaternion(), rate: new THREE.Vector3(),
    };
  }

  /** Each die's spot and radius, from the layout at the canvas's size. */
  place() {
    const worldPerPx = (2 * HALF_WIDTH) / this.width;
    for (const spot of shelfLayout(this.width, this.height)) {
      const die = this.dice.find((d) => d.sides === spot.sides);
      if (!die) continue;
      die.px = spot.x;
      die.py = spot.y;
      die.radius = spot.radius * worldPerPx;
      die.mesh.scale.setScalar(die.radius);
    }
  }

  setSize(width, height, dpr) {
    super.setSize(width, height, dpr, 0.5);
    this.width = width;
    this.height = height;
    this.place();
  }

  /** A client point in NDC over the canvas. */
  toNdc(clientX, clientY) {
    const r = this.renderer.domElement.getBoundingClientRect();
    return { x: ((clientX - r.left) / r.width) * 2 - 1, y: -(((clientY - r.top) / r.height) * 2 - 1) };
  }

  /** Where the ray through NDC (sx, sy) crosses height `y`, into `out`. */
  at(sx, sy, y, out) {
    out.set(sx, sy, 0.5).unproject(this.camera).sub(this.camera.position);
    const t = (y - this.camera.position.y) / out.y;
    return out.multiplyScalar(t).add(this.camera.position);
  }

  tick(dt) {
    const ease = Math.min(1, dt * 14);
    for (const die of this.dice) this.step(die, dt, ease);
    this.renderer.render(this.scene, this.camera);
  }

  /** One die's motion for the frame, and its place on the tile. */
  step(die, dt, ease) {
    const held = die === this.held;
    const hovered = die === this.hovered;
    const targetLift = held ? HELD_LIFT : hovered ? HOVER_LIFT : 0;
    die.lift += (targetLift - die.lift) * ease;
    // Turned off its resting pose the die may stand on any vertex, so the
    // further it is from rest the closer its clearance comes to a full radius.
    const turned = Math.min(1, (1 - Math.abs(die.turn.w)) * 4);
    const clearance = die.rest + Math.max(0, 1 - die.rest) * turned;
    const y = FLOOR_Y + (clearance + die.lift) * die.radius;
    this.at((die.px / this.width) * 2 - 1, 1 - (die.py / this.height) * 2, y, _v);
    die.mesh.position.copy(_v);

    if (hovered && !held) die.spin += dt * HOVER_SPIN;
    else if (die.spin !== 0) die.spin *= Math.max(0, 1 - dt * 6);

    if (!held) {
      // Momentum first, then the settle: the spin dies away and the pose
      // slides back to rest, so a let-go die tumbles a little before it lands.
      const speed = die.rate.length();
      if (speed > 1e-3) {
        _q.setFromAxisAngle(_axis.copy(die.rate).normalize(), speed * dt);
        die.turn.premultiply(_q);
        die.rate.multiplyScalar(Math.max(0, 1 - dt * SPIN_DRAG));
      } else {
        die.rate.set(0, 0, 0);
      }
      die.turn.slerp(_q.identity(), Math.min(1, dt * SETTLE));
    }
    _q.setFromAxisAngle(_up, die.spin);
    die.mesh.quaternion.copy(die.turn).multiply(_q).multiply(die.base);
  }

  /** The die under a client point, or null. */
  hitTest(clientX, clientY) {
    const { x, y } = this.toNdc(clientX, clientY);
    this.raycaster.setFromCamera(_ndc.set(x, y), this.camera);
    const found = this.raycaster.intersectObjects(this.dice.map((d) => d.mesh), false)[0];
    return found ? this.dice.find((d) => d.mesh === found.object) : null;
  }

  setHover(die) {
    this.hovered = die;
  }

  /** The pointer picks a die up: it floats high and turns under the pointer. */
  hold(die) {
    this.held = die;
    die.rate.set(0, 0, 0);
  }

  /** The pointer moved by (dx, dy) canvas pixels while holding: turn the die about the screen axes. */
  drag(dx, dy, dt) {
    const die = this.held;
    if (!die) return;
    const angle = Math.hypot(dx, dy) * DRAG_TURN;
    if (angle === 0) return;
    // Dragging right rolls the die about the screen's vertical, dragging down about its horizontal.
    _axis.set(dy, dx, 0).normalize();
    _q.setFromAxisAngle(_axis, angle);
    die.turn.premultiply(_q);
    // Remember the motion as spin, so a flick keeps turning after release.
    if (dt > 0) die.rate.copy(_axis).multiplyScalar(Math.min(12, angle / dt));
  }

  putDown() {
    this.held = null;
  }

  dispose() {
    this.disposed = true;
    for (const die of this.dice) {
      this.scene.remove(die.mesh);
      die.mesh.material.dispose();
    }
    this.dice = [];
    super.dispose();
    // `renderer.dispose()` leaves the context alive until GC; opening set
    // after set would stack them up against the browser's ceiling.
    this.renderer.forceContextLoss();
  }
}

/**
 * The still a skin's shelf tile shows: the set at rest, on a transparent
 * ground with its shadows, as a WebP blob. Baked through the same rig the
 * close-up runs, so the live dice land exactly over it. The lab calls this
 * with `sync` for a recipe under authoring; the client's dev hook bakes a
 * registered skin the same way the shelf would draw it.
 */
export async function bakeShelfPreview(skin, { width = SHELF_PREVIEW_WIDTH, height = SHELF_PREVIEW_HEIGHT, sync = false, quality = 0.9 } = {}) {
  if (sync) releaseSkinDieAssets(skin.id);
  const canvas = document.createElement('canvas');
  const rig = new DiceSetRig(canvas, skin, { sync });
  try {
    rig.setSize(width, height, 1);
    await rig.ready;
    rig.tick(0);
    return await new Promise((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('shelf preview: toBlob failed'))), 'image/webp', quality);
    });
  } finally {
    rig.dispose();
  }
}
